import { computed, Directive, inject, OnDestroy, Provider, Signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { ModuleConfig } from "@shared-config/moduleConfig";
import { AppHelper } from "@shared-helpers/appHelper";
import { FormHelper } from "@shared-helpers/formHelper";
import { GoogleAnalyticsHelper } from "@shared-helpers/googleAnalyticsHelper";
import { SubscriptionTracker } from "@shared-helpers/subscriptionTracker";
import { ConfigurationModel } from "@shared-model/configuration/ConfigurationModel";
import { MemberModel } from "@shared-model/member/MemberModel";
import { ModalModel } from "@shared-model/modal/ModalModel";
import { ModalType } from "@shared-model/modal/ModalType";
import { ConfigurationService } from "@shared-services/configuration.service";
import { ErrorService } from "@shared-services/error.service";
import { ExpertService } from "@shared-services/expert.service";
import { MemberService } from "@shared-services/member.service";
import "@web-app-base/JavaScript/ScriptLoader.js";

@Directive()
export abstract class BaseComponent implements OnDestroy {
  protected memberService = inject(MemberService);
  protected expertService = inject(ExpertService);
  protected errorService = inject(ErrorService);
  protected configurationService = inject(ConfigurationService);
  protected moduleConfig = inject(ModuleConfig);
  protected subscriptionTracker = inject(SubscriptionTracker);
  protected googleAnalyticsHelper = inject(GoogleAnalyticsHelper);

  config: Signal<ConfigurationModel> = computed(() => this.configurationService?.config(), { equal: AppHelper.objectsEqual });
  member: Signal<MemberModel> = computed(() => this.memberService?.member(), { equal: AppHelper.objectsEqual });
  memberLoggedIn: Signal<boolean> = computed(() => this.memberService?.memberLoggedIn(), { equal: AppHelper.objectsEqual });

  memberLoggedInObservable = toObservable(this.memberLoggedIn);

  // Using providers at component level ensures that there is one instance per component instance
  static componentProviders?: Provider[] = [
    { provide: SubscriptionTracker, useClass: SubscriptionTracker },
    { provide: FormHelper, useClass: FormHelper },
  ];

  get resources(): any {
    return this.config()?.ResourceData ?? {};
  }
  private modalData: ModalModel = null;
  get modal(): ModalModel {
    return this.modalData;
  }
  readonly friendlyName: string;

  get memberQueueExpertNickname(): string {
    if (!this.moduleConfig?.ExpertsSupported) return null;
    return this.expertService?.tryGetExpertNicknameFromCache(this.member()?.QueueExpertId);
  }

  constructor(friendlyName: string) {
    this.friendlyName = friendlyName;

    this.ensureConfigLoaded(() => {
      this.ensureMemberLoaded(() => { });
    });
  }

  ensureConfigLoaded(successCallback: () => void) {
    if (this.config()) {
      // Config already loaded
      successCallback();
    }
    else {
      this.subscriptionTracker?.add(this.configurationService?.getConfig((config) => {
        // Setting of data handled by signalConfig$
        successCallback();
      }));
    }
  }

  ensureMemberLoaded(successCallback: () => void) {
    if (this.member()) {
      // Member already loaded
      successCallback();
    }
    else {
      this.subscriptionTracker?.add(this.memberService?.getMember((member) => {
        // Setting of data handled by signalMember$
        successCallback();
      }));
    }
  }

  sessionStorageTryGet(key: string) : string {
    try {
      return sessionStorage.getItem(key);
    }
    catch (err) {
      this.errorService?.logError(`Failed to get sessionStorage key: '${key}', error: '${err}'`, `Angular ${this.friendlyName} sessionStorageTryGet`, err, true);
      return null;
    }
  }

  sessionStorageTrySet(key: string, value: string) {
    try {
      sessionStorage.setItem(key, value);
    }
    catch (err) {
      this.errorService?.logError(`Failed to set sessionStorage key: '${key}', error: '${err}'`, `Angular ${this.friendlyName} sessionStorageTrySet`, err, true);
    }
  }

  sessionStorageTryRemove(key: string) {
    try {
      sessionStorage.removeItem(key);
    }
    catch (err) {
      this.errorService?.logError(`Failed to remove sessionStorage key: '${key}', error: '${err}'`, `Angular ${this.friendlyName} sessionStorageTryRemove`, err, true);
    }
  }

  ensureExternalScriptLoaded(scriptPath: string, sucessCallback?: (scriptPath: string, event: Event) => void, errorCallback?: (scriptPath: string, event: Event) => void, versionRequired:boolean=true) {
    let scriptVersion: string = null;
    const config = this.config();
    if (config?.ScriptVersions) {
      // Try and get version from config
      scriptVersion = config.ScriptVersions[scriptPath];
    }
    if (versionRequired && !scriptVersion) {
      // No version found, generate new one to force load
      this.errorService?.logError(`Version not found for script: '${scriptPath}'`, `Angular ${this.friendlyName} ensureExternalScriptLoaded`, null, true);
      scriptVersion = Date.now().toString();
    }
    // Get script path with version if there is one
    let scriptPathAndVersion = scriptVersion ? `${scriptPath}?v=${scriptVersion}` : `${scriptPath}`;

    FourDScriptLoader?.requestScriptLoaded(
      scriptPathAndVersion,
      sucessCallback,
      errorCallback,
      scriptPath
    );
  }

  logError(message:string) {
    this.errorService?.logError(message, this.friendlyName, new Error(message));
  }

  logWarning(message: string) {
    this.errorService?.logError(message, this.friendlyName, new Error(message), true);
  }

  private constructModalInternal(message: string, type: ModalType, ActionOk: () => any, ActionYes: () => any, ActionNo: () => any, textBeforeLink?: string, linkHref?: string, linkText?: string, textAfterLink?: string) {
    let modal: ModalModel = new ModalModel();
    modal.Title = message;
    modal.Type = type;
    if (ActionOk) {
      modal.ActionOk = () => { this.modalData = null; ActionOk() };
    }
    if (ActionYes) {
      modal.ActionYes = () => { this.modalData = null; ActionYes() };
    }
    if (ActionNo) {
      modal.ActionNo = () => { this.modalData = null; ActionNo() };
    }
    modal.InfoLink = linkHref;
    modal.InfoLinkText = linkText;
    modal.InfoLinkBodyBeforeLink = textBeforeLink;
    modal.InfoLinkBodyAfterLink = textAfterLink;
    this.modalData = modal;
  }

  /**
  * Displays an Error modal with an OK button.
  * Sets data to the 'modal' property, a modal will only be shown if it exists in the component template.
  */
  constructModalError(message: string, ActionOk: () => any = () => { }) {
    this.constructModalInternal(message, ModalType.Error, ActionOk, null, null);
  }

  /**
  * Displays an Warning modal with an OK button.
  * Sets data to the 'modal' property, a modal will only be shown if it exists in the component template.
  */
  constructModalWarning(message: string, ActionOk: () => any = () => { }) {
    this.constructModalInternal(message, ModalType.Warning, ActionOk, null, null);
  }

  /**
  * Displays an Info modal with an OK button.
  * Sets data to the 'modal' property, a modal will only be shown if it exists in the component template.
  */
  constructModalInfo(message: string, ActionOk: () => any = () => { }) {
    this.constructModalInternal(message, ModalType.Info, ActionOk, null, null);
  }

  /**
  * Displays an Error modal with Yes and No buttons.
  * Sets data to the 'modal' property, a modal will only be shown if it exists in the component template.
  */
  constructModalErrorYesNo(message: string, ActionYes: () => any = () => { }, ActionNo: () => any = () => { }) {
    this.constructModalInternal(message, ModalType.Error, null, ActionYes, ActionNo);
  }

  /**
  * Displays an Warning modal with Yes and No buttons.
  * Sets data to the 'modal' property, a modal will only be shown if it exists in the component template.
  */
  constructModalWarningYesNo(message: string, ActionYes: () => any = () => { }, ActionNo: () => any = () => { }) {
    this.constructModalInternal(message, ModalType.Warning, null, ActionYes, ActionNo);
  }

  /**
  * Displays an Info modal with Yes and No buttons.
  * Sets data to the 'modal' property, a modal will only be shown if it exists in the component template.
  */
  constructModalInfoYesNo(message: string, ActionYes: () => any = () => { }, ActionNo: () => any = () => { }) {
    this.constructModalInternal(message, ModalType.Info, null, ActionYes, ActionNo);
  }

  /**
  * Displays an Info modal with a link and an OK button.
  * Sets data to the 'modal' property, a modal will only be shown if it exists in the component template.
  */
  constructModalInfoWithLink(message: string, textBeforeLink: string, linkHref: string, linkText: string, textAfterLink: string, ActionOk: () => any = () => { }) {
    this.constructModalInternal(message, ModalType.Info, ActionOk, null, null, textBeforeLink, linkHref, linkText, textAfterLink);
  }

  jsonObjectTryParseFromAttribute<T>(obj: any, attributeName: string): T {
    try {
      return AppHelper.parseJsonObject(obj);
    }
    catch (err) {
      this.errorService?.logError(`Failed to parse JSON object '${obj}' from attribute '${attributeName}', error: '${err}'`, `Angular ${this.friendlyName} jsonObjectTryParseFromAttribute`, err, false);
      return null;
    }
  }

  setActiveExpert(expertId: number): void {
    //console.log(`setActiveExpert ${expertId}`);
    this.expertService?.activeExpertId?.set(expertId);
  }

  removeActiveExpert(): void {
    //console.log("removeActiveExpert");
    this.expertService?.activeExpertId?.set(null);
  }

  handleClickEvent(event: Event, stopPropagation: boolean = true) {
    this.googleAnalyticsHelper?.handleClickEvent(event);
    if (stopPropagation) {
      event?.stopPropagation();
    }
  }

  ngOnDestroy() {
    // Component being destroyed
    // Tidy up all tracked subscriptions
    //console.log(`removeAll ${this.friendlyName}`)
    this.subscriptionTracker?.removeAll();
    this.subscriptionTracker = null;
  }

}
